<template>
    <nav id="family-portal-sidenav" class="overflow-hidden">
        <ul class="list-group-flush h-100 overflow-hidden d-flex flex-column">
            <li class="nav-item list-group-item message-list-itemm nav-list-header calls-list-header">
                <div class="d-flex align-items-center h-100">
                    <h5 class="fw-bold mb-0 ms-2 text-app-secondary">Channels</h5>
                    <div class="ms-auto d-flex gap-3">
                        <img
                            class="cursor-pointer"
                            src="../../../../../assets/images/chat/search-icon.svg"
                            v-tooltip="{ title: `Search`, placement: 'left' }"
                            @click="toggleSearchBox(true)"
                        />
                        <img
                            v-if="hasFamilyPortalEditPermission"
                            class="cursor-pointer"
                            src="../../../../../assets/images/chat/edit-icon.svg"
                            v-tooltip="{ title: `New Chat`, placement: 'left' }"
                            @click="navigateToNewConversation"
                        />
                    </div>
                </div>
            </li>
            <li class="nav-item list-group-item message-list-item" v-if="openSearch">
                <div class="input-group">
                    <input
                        type="text"
                        class="form-control border-end-0"
                        placeholder="Patient Name"
                        v-model="term"
                        v-focus
                    />
                    <span class="input-group-append cursor-pointer">
                        <div class="input-group-text bg-transparent px-2" @click="toggleSearchBox(false)">
                            <i class="fas fa-times dialog-close text-secondary font-size-sm"></i>
                        </div>
                    </span>
                </div>
            </li>
            <li
                v-if="!showTermSearchUi"
                class="nav-item list-group-item message-list-itemm nav-list-header groups-section"
            >
                <div class="d-flex align-items-center h-100">
                    <p class="fw-bold mb-0 ms-2 text-dark groups-title">Groups</p>
                    <div class="ms-auto d-flex gap-3">
                        <i class="fas fa-ellipsis-h font-size-lg ms-3 pt-1 ps-1 cursor-pointer"></i>
                    </div>
                </div>
            </li>
            <li class="nav-item list-group-item groups-item" v-if="conversationGroupsCount && !showTermSearchUi">
                <template v-for="[key, value] in enumToMap(GroupTypeEnum)" :key="key">
                    <div
                        v-if="hasGroupPermission(value.value)"
                        class="d-flex align-items-center cursor-pointer py-1 px-2"
                        :class="{ 'group-selected-active': value.value == selectedGroupType }"
                        @click="onGroupSelected(value.value)"
                    >
                        <p class="fw-bold mb-0 ms-2 text-primary fw-bold">{{ value.name }}</p>
                        <div
                            class="ms-auto d-flex gap-3"
                            v-if="conversationGroupsCount && conversationGroupsCount[key] > 0"
                        >
                            <span class="threads-count text-primary fw-bold">{{ conversationGroupsCount[key] }}</span>
                        </div>
                    </div>
                </template>
            </li>
            <li class="nav-item list-group-item selected-group" v-if="showTermSearchUi">
                <div class="d-flex align-items-center h-100">
                    <p class="mb-0 ms-2">Results</p>
                </div>
            </li>
            <li class="nav-item list-group-item selected-group" v-else-if="selectedGroupType">
                <div class="d-flex align-items-center h-100">
                    <p class="mb-0 ms-2">{{ GroupTypeEnum.getKeyUsingValue(selectedGroupType) }} Group</p>
                </div>
            </li>
            <div
                v-show="(!isLoading || pageNumber > 1) && groupConversations?.length > 0"
                ref="conversationListContainer"
                class="overflow-auto scroll-paginate flex-grow-1"
                style="min-height: 0"
                :class="{ 'custom-scrollbar': userOs == 'Windows' }"
            >
                <template v-if="(!isLoading || pageNumber > 1) && groupConversations?.length > 0">
                    <li
                        v-for="(conversation, index) in groupConversations"
                        :key="conversation.id"
                        :id="`conversation-${conversation.id}`"
                        class="nav-item list-group-item message-list-item cursor-pointer"
                        @click="handleConversationSelection(conversation, index)"
                        :class="{
                            active: selectedConversation.id == conversation.id
                        }"
                    >
                        <div v-if="showTermSearchUi" class="d-flex align-items-center min-width-0">
                            <div
                                class="ava-initials flex-shrink-0 me-1"
                                :class="`ava-initials-${getSolutionClassIndex(index)}`"
                            >
                                {{ formatUserInitials(conversation.patient) }}
                            </div>
                            <div class="ps-2 flex-grow-1 min-width-0">
                                <div class="d-flex align-items-center justify-content-between conversation-header-row">
                                    <span class="mb-1 text-black conversation-patient-name">
                                        <span>
                                            {{ formatDisplayUsers([conversation.patient]) }}
                                        </span>
                                    </span>
                                    <small class="text-dark conversation-time">
                                        {{ formatDateToLocalBasedOnDayMode(conversation.modifiedOn, true) }}
                                    </small>
                                </div>
                                <div class="d-flex justify-content-between align-items-center">
                                    <div class="text-truncate me-2 mb-1 text-dark">
                                        {{
                                            formatDisplayMessage(
                                                sanitizeMarkup(markdownToHtml(conversation.lastThread.body))
                                            )
                                        }}
                                    </div>
                                </div>
                                <div class="weight-600 blue-text">
                                    {{ GroupTypeEnum.getKeyUsingValue(conversation.conversationGroupType) }}
                                </div>
                            </div>
                        </div>
                        <div v-else class="d-flex align-items-center min-width-0">
                            <div
                                class="ava-initials flex-shrink-0 me-1"
                                :class="`ava-initials-${getSolutionClassIndex(index)}`"
                            >
                                {{ formatUserInitials(conversation.patient) }}
                            </div>
                            <div
                                class="ps-2 flex-grow-1 min-width-0"
                                :class="{
                                    'fw-bold': conversation.hasNewThreads
                                }"
                            >
                                <div class="d-flex align-items-center justify-content-between conversation-header-row">
                                    <span class="mb-1 text-dark conversation-patient-name">
                                        <span>
                                            {{ formatDisplayUsers([conversation.patient]) }}
                                        </span>
                                    </span>
                                    <small class="text-dark conversation-time">
                                        {{ formatDateToLocalBasedOnDayMode(conversation.lastThread.modifiedOn, true) }}
                                    </small>
                                </div>
                                <div class="d-flex justify-content-between align-items-center">
                                    <div class="text-truncate me-2 text-dark">
                                        <!-- :class="{
                                        'fw-bold': message?.unread > 0,
                                        'text-dark': !(message?.unread > 0)
                                    }" -->
                                        {{
                                            formatDisplayMessage(
                                                sanitizeMarkup(markdownToHtml(conversation.lastThread.subject))
                                            )
                                        }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>
                </template>
                <li class="py-1" v-show="isLoadingMoreConversations && pageNumber > 1">
                    <div class="loader-parent">
                        <div class="msg-loader"></div>
                    </div>
                </li>
            </div>
            <li
                v-if="!isLoading && groupConversations?.length == 0"
                class="nav-item list-group-item text-dark h-100 d-flex justify-content-center text-center flex flex-column"
            >
                <h5>No Conversations Found.</h5>
            </li>
            <div class="flex-grow-1 overflow-auto" style="min-height: 0">
                <template v-if="isLoading && pageNumber < 2">
                    <li
                        v-for="i in 5"
                        :key="i"
                        class="nav-item list-group-item d-flex justify-content-center text-middle flex flex-column"
                    >
                        <div class="d-flex align-items-center">
                            <div class="ava-initials me-1">
                                <span></span>
                            </div>
                            <div class="ms-2 flex-grow-1">
                                <span class="shimmer-loading w-90 lighten"></span>
                                <div class="message-content">
                                    <span class="shimmer-loading w-60 lighten"></span>
                                </div>
                            </div>
                        </div>
                    </li>
                </template>
            </div>
        </ul>
    </nav>
</template>

<style src="./FPSideNav.scss" />
<script lang="ts" src="./FPSideNav.ts" />
